import { gql, useMutation, useQuery } from '@apollo/client';
import IconOverflow from '@apollo/icons/small/IconOverflow.svg';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardTitle,
  Checkbox,
  IconButton,
  Link,
  Menu,
  MenuButton,
  MenuContent,
  MenuItem,
  MenuList,
  StatusBadge,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@apollo/orbit';
import omit from 'lodash/omit';
import React from 'react';
import {
  Link as ReactRouterLink,
  useHistory,
  useLocation,
} from 'react-router-dom';

import { Loading } from 'src/components/common/loading/Loading';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { URLModal } from 'src/components/common/URLModal';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import { PermissionWarning } from 'src/components/permissionGuards/permissionWarning/PermissionWarning';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { useCurrentPlan } from 'src/hooks/currentPlanV2Migration';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { assertUnreachable } from 'src/lib/assertUnreachable';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { DocsRouter } from 'src/lib/routers';
import { mergeQueryParams, setOverlayVisibleLocation } from 'src/lib/routing';

import {
  AlertUpsertModal,
  PERFORMANCE_ALERTS_QUERY,
  UPSERT_QUERY_TRIGGER_MUTATION,
  windowToCountMultiplier,
} from './AlertUpsertModal';

const comparisonOperatorMap: Record<GraphQLTypes.ComparisonOperator, string> = {
  GREATER_THAN: '>',
  LESS_THAN: '<',
  EQUALS: '=',
  GREATER_THAN_OR_EQUAL_TO: '>=',
  LESS_THAN_OR_EQUAL_TO: '<=',
  NOT_EQUALS: '!=',
  UNRECOGNIZED: '?',
};

const DELETE_QUERY_TRIGGER_MUTATION = gql`
  mutation DeleteQueryTriggerMutation($serviceId: ID!, $triggerId: ID!) {
    service(id: $serviceId) {
      deleteQueryTrigger(id: $triggerId)
    }
  }
`;

const AlertEnabledSwitch = ({
  serviceId,
  trigger,
}: {
  serviceId: string;
  trigger: GraphQLTypes.QueryTriggerFragment;
}) => {
  const [upsertTriggerMutation, { loading }] = useMutation<
    GraphQLTypes.UpsertQueryTriggerMutation,
    GraphQLTypes.UpsertQueryTriggerMutationVariables
  >(UPSERT_QUERY_TRIGGER_MUTATION);

  return (
    <Checkbox
      className="flex justify-center"
      isDisabled={loading}
      isChecked={trigger.enabled}
      onChange={() => {
        upsertTriggerMutation({
          variables: {
            serviceId,
            triggerId: trigger.id,
            trigger: {
              ...omit(trigger, [
                'channels',
                'enabled',
                '__typename',
                'id',
                'state',
              ]),
              enabled: !trigger.enabled,
              channelIds: trigger.channels?.map((c) => c.id),
            },
          },
        });
      }}
    />
  );
};

const AlertOverflowMenu = ({
  serviceId,
  trigger,
}: {
  serviceId: string;
  trigger: GraphQLTypes.QueryTriggerFragment;
}) => {
  const location = useLocation();
  const [upsertTriggerMutation, { loading: upsertLoading }] = useMutation<
    GraphQLTypes.UpsertQueryTriggerMutation,
    GraphQLTypes.UpsertQueryTriggerMutationVariables
  >(UPSERT_QUERY_TRIGGER_MUTATION, {
    refetchQueries: [
      {
        query: PERFORMANCE_ALERTS_QUERY,
        variables: {
          serviceId,
        },
      },
    ],
    awaitRefetchQueries: true,
  });

  const [deleteTriggerMutation, { loading: deleteLoading }] = useMutation<
    GraphQLTypes.DeleteQueryTriggerMutation,
    GraphQLTypes.DeleteQueryTriggerMutationVariables
  >(DELETE_QUERY_TRIGGER_MUTATION, {
    refetchQueries: [
      {
        query: PERFORMANCE_ALERTS_QUERY,
        variables: {
          serviceId,
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  return (
    <Menu placement="bottom-end" isLazy autoSelect={false}>
      <MenuButton
        as={IconButton}
        isLoading={upsertLoading || deleteLoading}
        variant="secondary"
      >
        <IconOverflow />
      </MenuButton>
      <MenuContent>
        <MenuList>
          <MenuItem
            as={ReactRouterLink}
            to={setOverlayVisibleLocation({
              location,
              overlayName: Config.modals.upsertAlert,
              additionalSearchParameters: { triggerId: trigger.id },
            })}
          >
            Edit
          </MenuItem>
          <MenuItem
            onClick={() => {
              upsertTriggerMutation({
                variables: {
                  serviceId,
                  triggerId: undefined, // new trigger
                  trigger: {
                    ...omit(trigger, [
                      'channels',
                      'enabled',
                      '__typename',
                      'id',
                      'state',
                    ]),
                    enabled: trigger.enabled,
                    channelIds: trigger.channels?.map((c) => c.id),
                  },
                },
              });
            }}
          >
            Duplicate
          </MenuItem>
          <MenuItem
            className="text-error hover:bg-error hover:text-error"
            onClick={() => {
              // eslint-disable-next-line no-alert
              const confirmed = confirm(
                'Are you sure you want to delete this notification trigger?',
              );

              if (confirmed) {
                deleteTriggerMutation({
                  variables: { serviceId, triggerId: trigger.id },
                });
              }
            }}
          >
            Delete
          </MenuItem>
        </MenuList>
      </MenuContent>
    </Menu>
  );
};

export const Alerts = ({
  accountId,
  serviceId,
}: {
  accountId: string;
  serviceId: string;
}) => {
  const { data, loading: queryLoading } = useQuery(PERFORMANCE_ALERTS_QUERY, {
    variables: {
      serviceId,
    },
    context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    pollInterval: 5000,
  });
  const currentPlanResults = useCurrentPlan({ accountId });
  const hasDatadogCapabilities = !!currentPlanResults?.currentPlan?.datadog;

  const location = useLocation();
  const history = useHistory();

  if (queryLoading) return <Loading />;

  return (
    <>
      <URLModal name={Config.modals.upsertAlert}>
        {({ isOpen }) => (
          <AlertUpsertModal
            isOpen={isOpen}
            onClose={() => {
              history.push({
                ...location,
                search: mergeQueryParams(location.search, {}, [
                  'triggerId',
                  'overlay',
                ]),
              });
            }}
            graphId={serviceId}
            queryTriggers={data?.service?.queryTriggers ?? undefined}
          />
        )}
      </URLModal>

      <Card>
        <CardHeader>
          <CardTitle>
            Performance alerts
            <Tooltip
              interactive
              label={
                <p>
                  Studio's notifications system is experimental and is not meant
                  to replace other production alerting infrastructure you may
                  have set up. We do not currently make any guarantees on the
                  reliability of this feature.{' '}
                  {hasDatadogCapabilities ? (
                    <>
                      If you need reliable alerting based on your graph's usage
                      data in Studio, we recommend using our{' '}
                      <Link
                        isExternal
                        to={DocsRouter.path('DatadogIntegration')}
                      >
                        Datadog forwarder
                      </Link>{' '}
                      to forward your metrics data to Datadog and setting up
                      alerting in their system.
                    </>
                  ) : null}
                </p>
              }
            >
              <Badge variant="neutralOutlined">Experimental</Badge>
            </Tooltip>
          </CardTitle>
          <CardDescription>
            Studio will notify you when any of the thresholds below are crossed.
            Notifications are triggered based on the metrics data from your
            graph and evaluated over a sliding window of the last 5 minutes.
          </CardDescription>
          <PermissionWarning
            graphId={serviceId}
            graphPermissions="canManageIntegrations"
            childRenderer={(hasPermission) => (
              <Button
                variant="secondary"
                isDisabled={!hasPermission}
                as={ReactRouterLink}
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.upsertAlert,
                })}
              >
                Add a new alert
              </Button>
            )}
          />
        </CardHeader>
        <CardBody>
          <PermissionGuard
            graphId={serviceId}
            graphPermissions={['canQueryStats', 'canQueryPrivateInfo']}
          >
            {data?.service?.queryTriggers &&
            data?.service?.queryTriggers.length > 0 ? (
              <TableContainer>
                <Table>
                  <Thead>
                    <Tr>
                      <Th>Operation</Th>
                      <Th>Trigger</Th>
                      <Th>Channel</Th>
                      <Th>Variant</Th>
                      <Th className="whitespace-nowrap">Last triggered</Th>
                      <Th className="whitespace-nowrap">Last evaluated</Th>
                      <Th>Status</Th>
                      <Th isFormElement>Enabled</Th>
                      <Th className="w-px">
                        <span className="sr-only">Actions</span>
                      </Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(data.service.queryTriggers ?? []).map((t) => {
                      return (
                        <Tr key={t.id}>
                          <Td>
                            {t.operationNames.length > 0
                              ? t.operationNames.join(', ')
                              : 'All operations'}
                          </Td>
                          <Td>
                            {t.metric ===
                            GraphQLTypes.QueryTriggerMetric.ERROR_COUNT
                              ? 'Error rate'
                              : t.metric ===
                                  GraphQLTypes.QueryTriggerMetric.REQUEST_COUNT
                                ? 'Request rate'
                                : t.metric ===
                                    GraphQLTypes.QueryTriggerMetric
                                      .ERROR_PERCENTAGE
                                  ? 'Error percentage'
                                  : t.metric ===
                                      GraphQLTypes.QueryTriggerMetric
                                        .REQUEST_SERVICE_TIME
                                    ? `${
                                        t.percentile
                                          ? `p${Math.floor(t.percentile * 100)}`
                                          : 'Unknown'
                                      } service time`
                                    : assertUnreachable(t.metric)}{' '}
                            {comparisonOperatorMap[t.comparisonOperator]}{' '}
                            {t.metric ===
                            GraphQLTypes.QueryTriggerMetric.ERROR_PERCENTAGE
                              ? t.threshold * 100
                              : t.metric ===
                                    GraphQLTypes.QueryTriggerMetric
                                      .ERROR_COUNT ||
                                  t.metric ===
                                    GraphQLTypes.QueryTriggerMetric
                                      .REQUEST_COUNT
                                ? t.threshold /
                                  windowToCountMultiplier[
                                    GraphQLTypes.QueryTriggerWindow.FIVE_MINUTES
                                  ]
                                : t.threshold}
                            {t.metric ===
                            GraphQLTypes.QueryTriggerMetric.ERROR_PERCENTAGE
                              ? '%'
                              : t.metric ===
                                    GraphQLTypes.QueryTriggerMetric
                                      .ERROR_COUNT ||
                                  t.metric ===
                                    GraphQLTypes.QueryTriggerMetric
                                      .REQUEST_COUNT
                                ? 'rpm'
                                : t.metric ===
                                    GraphQLTypes.QueryTriggerMetric
                                      .REQUEST_SERVICE_TIME
                                  ? 'ms'
                                  : assertUnreachable(t.metric)}
                          </Td>
                          <Td>
                            {t.channels.length > 0
                              ? t.channels.map((c) => c.name).join(', ')
                              : 'None'}
                          </Td>
                          <Td>{t.variant ?? 'All variants'}</Td>
                          <Td>
                            {t.state.lastTriggeredAt ? (
                              <LocalTimestamp value={t.state.lastTriggeredAt} />
                            ) : (
                              'Never'
                            )}
                          </Td>
                          <Td>
                            {t.state.evaluatedAt ? (
                              <LocalTimestamp value={t.state.evaluatedAt} />
                            ) : (
                              'Never'
                            )}
                          </Td>
                          <Td>
                            <StatusBadge
                              variant="hidden"
                              colorScheme={t.state.triggered ? 'red' : 'green'}
                            >
                              {t.state.triggered ? 'Triggered' : 'Watching'}
                            </StatusBadge>
                          </Td>
                          <Td isFormElement>
                            <AlertEnabledSwitch
                              trigger={t}
                              serviceId={serviceId}
                            />
                          </Td>
                          <Td isActionsMenu>
                            <AlertOverflowMenu
                              trigger={t}
                              serviceId={serviceId}
                            />
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </TableContainer>
            ) : (
              'Your graph does not have any performance alerts yet.'
            )}
          </PermissionGuard>
        </CardBody>
      </Card>
    </>
  );
};
